import React, { useMemo } from 'react';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_800,
  COLOR_SECONDARY_1_300,
  COLOR_SECONDARY_1_600,
} from 'src/utils/colors';
import BarDataCard from 'src/App/Dashboards/Market/Components/Cards/BarDataCard';
import { VisibleListingsProp } from 'src/interfaces/iproptypes';
import { Grid, useTheme } from '@mui/material';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';
import { formatCurrency, getAmenityDistribution } from 'src/utils/functions';

const AmenityCard = ({
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  noDataInMarket,
  totalListingsCount,
}: {
  cachedData?: { [key: string]: number } | null;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  noDataInMarket: boolean;
  totalListingsCount: number;
}) => {
  const theme = useTheme();
  const isDataStatic = viewMode === 'dashboard' && cachedData;
  const isDataLoading = multipleMarkersCallLoading && !isDataStatic;

  const amenityDistribution = useMemo(() => {
    if (isDataLoading) {
      return [];
    }
    return getAmenityDistribution(cachedData as { [key: string]: number });
  }, [cachedData]);

  if (isDataLoading || isDynamicLoading) {
    return <LoadingCard height={150} type="bars" />;
  }

  if (noDataInMarket) {
    return <NoDataCard height={150} borderRadius={8} />;
  }

  const getTooltipSuffix = (value: number, key: string) => {
    const numberOfListing = formatCurrency(String(Math.round(totalListingsCount * (value / 100))));
    return `${numberOfListing} listings ( ${value}% )`;
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      sx={{ boxShadow: '0 4px 10px #0003', borderRadius: 1, ...theme.utils.cardPadding }}
    >
      <BarDataCard
        title="Number of Listings by Amenities"
        tooltipContent="The horizontal bars show the percentage of listings in your current map view that have that specific amenity or service."
        data={amenityDistribution}
        orientation="vertical"
        unit="%"
        label="%"
        graphHeight={459} // this should be dynamic based on the height of the container
        barColor={COLOR_PRIMARY_1_600}
        barAccentColor={COLOR_PRIMARY_1_800}
        getTooltipSuffix={getTooltipSuffix}
        parent="listing_by_amenities"
      />
    </Grid>
  );
};

export default React.memo(AmenityCard);
